import type { PaperWithRole } from "@/types/paper";

interface Props {
  papers: PaperWithRole[];
  activePaperId?: string | null;
  onRowClick: (paper: PaperWithRole) => void;
  /** When true, renders a checkbox column. Selection state lives outside
   * (in the comparison store); this component is presentational. */
  selectionMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (paperId: string) => void;
}

interface RoleBadge {
  label: string;
  color: string;
}

function roleBadge(p: PaperWithRole): RoleBadge {
  if (p.paper_role === "foundational") {
    return { label: "Seed", color: "#a6e3a1" };
  }
  switch (p.argumentation_role) {
    case "supports":
      return { label: "Supports", color: "#a6e3a1" };
    case "challenges":
      return { label: "Counter", color: "#f38ba8" };
    case "synthesis":
      return { label: "Synth.", color: "#cba6f7" };
    case "extends":
      return { label: "Extends", color: "#74c7ec" };
    case "applies":
      return { label: "Applies", color: "#74c7ec" };
    case "operationalises":
      return { label: "Op.", color: "#74c7ec" };
    case "reviews":
      return { label: "Reviews", color: "#74c7ec" };
    default:
      return { label: "—", color: "#6c7086" };
  }
}

function scoreClass(score: number): string {
  if (score >= 80) return "bg-emerald-500/20 text-emerald-400";
  if (score >= 70) return "bg-amber-500/20 text-amber-400";
  return "bg-muted text-muted-foreground";
}

function authorPreview(authors: string[]): string {
  if (authors.length === 0) return "Unknown";
  if (authors.length === 1) return authors[0];
  if (authors.length === 2) return `${authors[0]} & ${authors[1]}`;
  return `${authors[0]} et al`;
}

export function AnalyzePaperList({
  papers,
  activePaperId,
  onRowClick,
  selectionMode = false,
  selectedIds,
  onToggleSelect,
}: Props) {
  const gridCols = selectionMode ? "24px 1fr 50px 80px" : "1fr 50px 80px";

  return (
    <div className="flex h-full flex-col">
      <div
        className="grid items-center gap-2.5 border-b px-3 py-2 text-[9.5px] font-bold uppercase tracking-wider text-muted-foreground"
        style={{ gridTemplateColumns: gridCols }}
      >
        {selectionMode && <span aria-hidden />}
        <span>Paper</span>
        <span className="text-right">Score</span>
        <span>Role</span>
      </div>
      <div className="flex-1 overflow-y-auto">
        {papers.length === 0 ? (
          <div className="flex h-full items-center justify-center px-4 text-xs text-muted-foreground">
            No papers in scope.
          </div>
        ) : (
          papers.map((p) => {
            const role = roleBadge(p);
            const score = Math.round(
              p.user_score_override ?? p.normalized_score ?? 0,
            );
            const isActive = activePaperId === p.id;
            const isSelected = selectionMode && selectedIds?.has(p.id);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => onRowClick(p)}
                className={`grid w-full items-center gap-2.5 border-b px-3 py-2 text-left transition-colors hover:bg-accent/40 ${
                  isActive ? "bg-accent/60" : ""
                } ${isSelected ? "bg-indigo-500/10" : ""}`}
                style={{ gridTemplateColumns: gridCols }}
              >
                {selectionMode && (
                  <input
                    type="checkbox"
                    checked={isSelected ?? false}
                    onChange={(e) => {
                      e.stopPropagation();
                      onToggleSelect?.(p.id);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Select ${p.title}`}
                    className="size-3.5 cursor-pointer"
                  />
                )}
                <div className="min-w-0">
                  <div className="text-xs leading-snug line-clamp-2">{p.title}</div>
                  <div className="mt-0.5 text-[9.5px] text-muted-foreground">
                    {authorPreview(p.authors)}
                    {p.year != null ? ` · ${p.year}` : ""} · {p.citations} cites
                  </div>
                </div>
                <span
                  className={`inline-block rounded px-1.5 py-px text-center text-[10px] font-bold tabular-nums ${scoreClass(score)}`}
                >
                  {score || "—"}
                </span>
                <span className="flex items-center gap-1 text-[10.5px]">
                  <span
                    className="inline-block size-2 rounded-full flex-none"
                    style={{ backgroundColor: role.color }}
                  />
                  <span className="truncate">{role.label}</span>
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
